import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon, PrismInput } from '@mylighthouse/prism-ember';
import { not } from 'ember-truth-helpers';

interface GeneralTablesCellsCellEditableSignature {
  Args: {
    displayValue: string;
    hasEditRights: boolean;
    inputValue: string;
    saveAction: (value: string) => void;
  };
}

export default class GeneralTablesCellsCellEditable extends Component<GeneralTablesCellsCellEditableSignature> {
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked inputValue: string = this.args.inputValue;
  @tracked isEditing: boolean = false;

  @action enterEditState(): void {
    if (!this.args.hasEditRights) {
      return;
    }

    this.isEditing = true;
  }

  @action handleBlur(): void {
    // Don't save (again) when triggered by Enter or Escape key press
    if (!this.isEditing) {
      return;
    }

    this.isEditing = false;
    this.args.saveAction(this.inputValue);
  }

  @action handleKeyPress(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter': {
        this.isEditing = false;
        this.args.saveAction(this.inputValue);

        break;
      }

      case 'Escape': {
        this.isEditing = false;

        break;
      }
    }
  }

  @action updateInputValue(event: Event): void {
    this.inputValue = (event.target as HTMLInputElement).value;
  }

  <template>
    {{! template-lint-disable no-nested-interactive require-presentational-children }}
    <div class="cell-editable-container text-300--regular" role="tooltip">
      {{#if @hasEditRights}}
        {{#if this.isEditing}}
          <PrismInput
            @autofocus={{true}}
            @disabled={{not @hasEditRights}}
            @onFocusOut={{this.handleBlur}}
            @onInput={{this.updateInputValue}}
            @onKeyDown={{this.handleKeyPress}}
            @value={{this.inputValue}}
          />
        {{else}}
          <div
            class="cell-editable-value-container"
            role="button"
            {{on "click" this.enterEditState}}
          >
            <span class="cell-editable-value truncate">{{@displayValue}}</span>
            <div class="cell-editable-icon">
              <PrismIcon @name="edit-pencil" @size="small" />
            </div>
          </div>
        {{/if}}
      {{else}}
        {{@displayValue}}
      {{/if}}
    </div>
  </template>
}
